import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Bug,
  CalendarCheck,
  CircleDot,
  FileSearch,
  FolderCog,
  House,
  Info,
  ListChecks,
  ListOrdered,
  ListTodo,
  MessagesSquare,
  PenLine,
  Sparkles,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import { retryTransientOnce } from "@/hooks/queries/use-work-items";
import { useMe } from "@/hooks/queries/use-me";
import { useNextUpProject } from "@/hooks/queries/use-next-up";
import { canAnywhere, canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { groomingApi, type GroomingBacklogItem } from "@/lib/api/grooming";
import { ITEM_DETAIL_STATE, workItemPagePath } from "@/lib/api/work-items";
import { GROOMING_QUEUE_PATH, groomingPath, projectFromPath } from "@/lib/grooming/paths";
import {
  ASK_PATH,
  ASK_TOOL_NAME,
  FOGBUGZ_FORMULATOR_PATH,
  FOGBUGZ_FORMULATOR_TOOL_NAME,
  FOGBUGZ_PATH,
  FOGBUGZ_TO_ADO_PATH,
  FOGBUGZ_TO_ADO_TOOL_NAME,
  FOGBUGZ_TOOL_NAME,
  FORMULATOR_PATH,
  fogbugzToAdoVisible,
  rolesPath,
  TOOLBOX_HOME_PATH,
} from "@/lib/toolbox/tools";
import { cn } from "@/lib/utils";
import {
  registeredCommands,
  useCommandPaletteStore,
  type GroomingCommand,
} from "./use-command-registry";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/** At most this many backlog items are listed for a search. */
const MAX_ITEM_RESULTS = 8;

/** An item typed or pasted into the palette: its ID, and the ADO project when it came from a link. */
export interface ParsedItemLink {
  id: string;
  /** The project named by a pasted ADO link (decoded); absent for an ID, AB#, or a link without one. */
  project?: string;
}

/** The section heading above a pasted link's row (frame D5). */
export const LINK_SECTION = "Item from the link";

/** The palette footer's hint while the query is a link to a project (frame D5). */
export const LINK_FOOTER_HINT = "No role on a link's project? It says so and offers My ADO Items instead.";

/**
 * The project segment of an ADO work item link, decoded, or undefined.
 * `https://dev.azure.com/{org}/{project}/_workitems/edit/{id}` and
 * `https://{org}.visualstudio.com/{project}/_workitems/edit/{id}` (also with
 * the legacy collection segment, `…visualstudio.com/DefaultCollection/{project}/…`);
 * on any other host (Azure DevOps Server) the segment before `_workitems`. A link
 * without a project segment (`dev.azure.com/{org}/_workitems/...`) has none.
 */
function projectOfLink(text: string): string | undefined {
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return undefined;
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const at = segments.findIndex((segment) => segment.toLowerCase() === "_workitems");
  if (at < 0) return undefined;
  const host = url.hostname.toLowerCase();
  const expected = host === "dev.azure.com" ? 1 : at - 1;
  // visualstudio.com: the project first, or after the legacy collection segment.
  if (host.endsWith(".visualstudio.com") && at - 1 > 1) return undefined;
  if (expected < 0 || at - 1 !== expected) return undefined;
  const raw = segments[expected];
  if (!raw) return undefined;
  try {
    const project = decodeURIComponent(raw).trim();
    return project.length > 0 ? project : undefined;
  } catch {
    return undefined;
  }
}

/**
 * An item typed or pasted into the palette: `24597` or `AB#24597` give the
 * ID; an ADO work item link (`…/{project}/_workitems/edit/24597`) gives the
 * ID and the link's project (epic PI-SPEC-SW-E292, feature PI-SPEC-SW-1495,
 * UX-PM-17). Pure; exported for tests.
 */
export function parseItemLink(query: string): ParsedItemLink | undefined {
  const text = query.trim();
  const plain = /^(?:ab#)?(\d{1,9})$/i.exec(text);
  if (plain?.[1]) return { id: plain[1] };
  const link = /_workitems\/edit\/(\d{1,9})/i.exec(text);
  if (!link?.[1]) return undefined;
  const project = projectOfLink(text);
  return project ? { id: link[1], project } : { id: link[1] };
}

/** The item ID from {@link parseItemLink}, without the project. Pure; exported for tests. */
export function parseItemId(query: string): string | undefined {
  return parseItemLink(query)?.id;
}

function sameProject(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/** Every word of the query appears in the text. */
function matches(text: string, query: string): boolean {
  const haystack = text.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

/** Backlog items whose ID or title matches the query. Pure; exported for tests. */
export function matchBacklogItems(
  items: readonly Pick<GroomingBacklogItem, "itemId" | "title">[],
  query: string,
  limit = MAX_ITEM_RESULTS
): Pick<GroomingBacklogItem, "itemId" | "title">[] {
  const text = query.trim().replace(/^ab#/i, "");
  if (!text) return [];
  return items.filter((item) => item.itemId.includes(text) || matches(item.title, text)).slice(0, limit);
}

interface ViewCommand {
  page: "backlog" | "session" | "one" | "project";
  label: string;
  icon: LucideIcon;
  /** Shown only with `map-project` on the project (E283). */
  mapOnly?: boolean;
}

const VIEW_COMMANDS: readonly ViewCommand[] = [
  { page: "backlog", label: "Backlog", icon: ListOrdered },
  { page: "session", label: "Session", icon: CalendarCheck },
  { page: "one", label: "One item", icon: Sparkles },
  // PKG-PM-F (UX-PM-15): the Project page replaced Repos; it opens on the Meeting schedule.
  { page: "project", label: "Project", icon: FolderCog, mapOnly: true },
];

/**
 * The grooming command palette (epic PI-SPEC-SW-E280; design `.pal`).
 * Cmd/Ctrl+K toggles it; ↑/↓ move, Enter runs, Esc closes. Commands: go to
 * each view the person can use, "Groom one item by ID or link…" (with
 * `groom-one`), the Queue (with `read-backlog` on some project; epic
 * PI-SPEC-SW-E284), the Formulator (with `formulator` on some project), the
 * FogBugz event log summary (with `fogbugz` on some project; epic
 * PI-SPEC-SW-E307), Roles
 * (with `assign-roles` on some project; PI-SPEC-SW-1431), the
 * commands pages register (see `useRegisterCommands`), and a jump to any
 * backlog item by ID or title. The project's whole backlog (`top=all`) is
 * read on the first open only. Mounted once: by the Toolbox frame inside
 * SpecTree Toolbox, by AppLayout on grooming pages in a browser.
 *
 * A pasted ADO link to another project's item (epic PI-SPEC-SW-E292, feature
 * PI-SPEC-SW-1495, UX-PM-17, frame D5) is offered first, under "Item from the
 * link": "Open AB#<id> in <project>" switches to that project's Backlog with
 * the item open; without `read-backlog` there the row says there is no
 * Toolbox role and opens My ADO Items instead. On Toolbox home, which has no
 * project in its path, IDs and titles are looked up in home's current
 * project: the last project shown on this computer while the person may still
 * read it, else the first project they may read (`useNextUpProject`, feature
 * PI-SPEC-SW-1499), which the title-bar search names.
 */
export function GroomingCommandPalette() {
  const open = useCommandPaletteStore((state) => state.open);
  const setOpen = useCommandPaletteStore((state) => state.setOpen);
  const toggle = useCommandPaletteStore((state) => state.toggle);
  const sources = useCommandPaletteStore((state) => state.sources);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const project = projectFromPath(pathname);
  const onHome = pathname === TOOLBOX_HOME_PATH;
  const { data: me } = useMe();
  const { data: access } = useToolboxAccess();
  // Toolbox home has no project in its path: items are looked up in home's current project (the last one shown
  // while still readable, else the first readable one), the same project the title-bar search names.
  const homeProject = useNextUpProject();
  const itemProject = project ?? (onHome ? homeProject : undefined);
  const mayMap = canOn(access, project, "map-project");
  const mayGroomHere = canOn(access, project, "groom-one");
  const mayGroomAnywhere = canAnywhere(access, "groom-one");
  const mayFormulate = canAnywhere(access, "formulator");
  const mayFogbugz = canAnywhere(access, "fogbugz");
  const mayAsk = canAnywhere(access, "ask");
  const bridge = useToolboxBridge();
  const mayBringToAdo = fogbugzToAdoVisible(canAnywhere(access, "fogbugz-to-ado"), me?.hasFogbugzKey ?? false, bridge);
  const mayReadQueue = canAnywhere(access, "read-backlog");
  const mayAssignRoles = canAnywhere(access, "assign-roles");
  const rolesProject = project && canOn(access, project, "assign-roles") ? project : undefined;
  const groomingVisible = me?.groomingVisible ?? false;
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [backlogWanted, setBacklogWanted] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [toggle]);

  useEffect(() => {
    if (open) setBacklogWanted(true);
    setQuery("");
    setSelected(0);
  }, [open]);

  const backlog = useQuery({
    // ADO backlog order, as before the Backlog page's sort menu (E280, PI-SPEC-SW-1406).
    queryKey: groomingKeys.backlog(itemProject ?? "", "all", "rank"),
    queryFn: () => groomingApi.backlog(itemProject ?? "", "all", "rank"),
    select: (response) => response.data,
    enabled: Boolean(itemProject) && backlogWanted,
    staleTime: 60 * 1000,
    retry: retryTransientOnce,
  });

  const results = useMemo((): GroomingCommand[] => {
    const go = (path: string, state?: typeof ITEM_DETAIL_STATE) => () => {
      void navigate(path, state ? { state } : undefined);
    };
    const builtIn: GroomingCommand[] = project
      ? [
          ...VIEW_COMMANDS.filter((view) => !view.mapOnly || mayMap).map((view) => ({
            id: `go-${view.page}`,
            label: `Go to ${view.label}`,
            icon: view.icon,
            run: go(groomingPath(project, view.page)),
          })),
          ...(mayGroomHere
            ? [
                {
                  id: "groom-one",
                  label: "Groom one item by ID or link…",
                  icon: Sparkles,
                  keywords: "brief single",
                  run: go(groomingPath(project, "one")),
                },
              ]
            : []),
        ]
      : groomingVisible
        ? [
            { id: "go-grooming", label: "Go to Grooming", icon: ListChecks, run: go("/grooming") },
            ...(mayGroomAnywhere
              ? [{ id: "groom-one", label: "Groom one item by ID or link…", icon: Sparkles, run: go("/grooming/one") }]
              : []),
          ]
        : [];
    // The run queue (epic PI-SPEC-SW-E284): one per computer, across projects.
    const queue: GroomingCommand[] = mayReadQueue
      ? [{ id: "go-queue", label: "Go to Queue", icon: ListTodo, keywords: "running up next completed", run: go(GROOMING_QUEUE_PATH) }]
      : [];
    // Other tools (epic PI-SPEC-SW-E282): the Formulator, then the Toolbox home.
    const commands = [
      ...builtIn,
      ...queue,
      ...registeredCommands(sources),
      ...(mayFormulate ? [{ id: "go-formulator", label: "Go to ADO Formulator", icon: PenLine, run: go(FORMULATOR_PATH) }] : []),
      ...(mayFogbugz
        ? [
            {
              id: "go-fogbugz-formulator",
              label: `Go to ${FOGBUGZ_FORMULATOR_TOOL_NAME}`,
              icon: Bug,
              keywords: "case report bug feature question new",
              run: go(FOGBUGZ_FORMULATOR_PATH),
            },
            {
              id: "go-fogbugz",
              label: `Go to ${FOGBUGZ_TOOL_NAME}`,
              icon: FileSearch,
              keywords: "evtx case windows",
              run: go(FOGBUGZ_PATH),
            },
          ]
        : []),
      ...(mayBringToAdo
        ? [
            {
              id: "go-fogbugz-to-ado",
              label: `Go to ${FOGBUGZ_TO_ADO_TOOL_NAME}`,
              icon: ArrowRightLeft,
              keywords: "case bug board azure devops bring",
              run: go(FOGBUGZ_TO_ADO_PATH),
            },
          ]
        : []),
      ...(mayAsk
        ? [
            {
              id: "go-ask",
              label: `Go to ${ASK_TOOL_NAME}`,
              icon: MessagesSquare,
              keywords: "ask product question how does code spec",
              run: go(ASK_PATH),
            },
          ]
        : []),
      ...(mayAssignRoles
        ? [
            {
              id: "go-roles",
              label: "Go to Roles",
              icon: UsersRound,
              keywords: "people permissions assign project manager",
              run: go(rolesPath(rolesProject)),
            },
          ]
        : []),
      { id: "toolbox-home", label: "Go to Toolbox home", icon: House, run: go(TOOLBOX_HOME_PATH) },
    ];

    const text = query.trim();
    if (!text) return commands;

    const out: GroomingCommand[] = [];
    const parsed = parseItemLink(text);
    const linkProject = parsed?.project;
    const elsewhere = parsed && linkProject && !(itemProject && sameProject(linkProject, itemProject)) ? linkProject : undefined;
    // A link to another project's item comes first and opens it there (frame D5).
    if (parsed && elsewhere) {
      if (canOn(access, elsewhere, "read-backlog")) {
        out.push({
          id: `item-link-${parsed.id}`,
          label: `Open AB#${parsed.id} in ${elsewhere}`,
          emphasis: elsewhere,
          description: itemProject
            ? `This link is to another project. Opening it switches the project from ${itemProject}.`
            : "This link is to another project. Opening it switches to that project.",
          section: LINK_SECTION,
          icon: ArrowRightLeft,
          iconClassName: "text-[#6CC4FF]",
          run: go(groomingPath(elsewhere, `items/${parsed.id}`)),
        });
      } else {
        out.push({
          id: `item-link-${parsed.id}`,
          label: `AB#${parsed.id} is in ${elsewhere}, where you have no Toolbox role`,
          emphasis: elsewhere,
          action: "Open in My ADO Items",
          section: LINK_SECTION,
          icon: ArrowRightLeft,
          iconClassName: "text-[#6CC4FF]",
          run: go(workItemPagePath({ container: elsewhere, id: parsed.id }), ITEM_DETAIL_STATE),
        });
      }
    }
    const typedId = itemProject && !elsewhere ? parsed?.id : undefined;
    if (itemProject && typedId) {
      // The title when the loaded backlog has the item, so the person sees which item it is (PKG-PM-D Mac walk, 2026-10-23).
      const known = backlog.data?.items.find((item) => item.itemId === typedId);
      out.push({
        id: `item-${typedId}`,
        label: known?.title ?? `Open item AB#${typedId}`,
        hint: `AB#${typedId}`,
        icon: CircleDot,
        run: go(groomingPath(itemProject, `items/${typedId}`)),
      });
    }
    out.push(...commands.filter((command) => matches(`${command.label} ${command.keywords ?? ""}`, text)));
    if (itemProject && backlog.data && !elsewhere) {
      for (const item of matchBacklogItems(backlog.data.items, text)) {
        if (item.itemId === typedId) continue;
        out.push({
          id: `item-${item.itemId}`,
          label: item.title,
          hint: `AB#${item.itemId}`,
          icon: CircleDot,
          run: go(groomingPath(itemProject, `items/${item.itemId}`)),
        });
      }
    }
    return out;
  }, [
    project,
    itemProject,
    access,
    mayMap,
    mayGroomHere,
    mayGroomAnywhere,
    mayFormulate,
    mayFogbugz,
    mayAsk,
    mayBringToAdo,
    mayReadQueue,
    mayAssignRoles,
    rolesProject,
    groomingVisible,
    sources,
    query,
    backlog.data,
    navigate,
  ]);

  if (!open) return null;

  const active = Math.min(selected, Math.max(0, results.length - 1));
  const linkHint = Boolean(parseItemLink(query)?.project);

  const close = () => {
    setOpen(false);
  };

  const run = (command: GroomingCommand | undefined) => {
    if (!command) return;
    close();
    command.run();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelected(results.length === 0 ? 0 : (active + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelected(results.length === 0 ? 0 : (active - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(results[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-black/45 pt-[90px] [-webkit-app-region:no-drag]"
      onClick={close}
      data-testid="grooming-command-palette"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search items or jump to"
        className="w-[600px] max-w-[calc(100vw-32px)] overflow-hidden rounded-[14px] border border-[#27272A] bg-[rgba(17,17,19,.96)] shadow-[0_30px_80px_rgba(0,0,0,.6)]"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <input
          autoFocus
          aria-label="Search items, or type a command"
          aria-controls="grooming-palette-results"
          aria-activedescendant={results[active] ? `palette-${results[active].id}` : undefined}
          className="h-[50px] w-full border-0 border-b border-[#1F1F23] bg-transparent px-[18px] text-[15px] text-[#FAFAFA] outline-none placeholder:text-[#8B8B94]"
          placeholder="Search items, or type a command…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setSelected(0);
          }}
          onKeyDown={onKeyDown}
        />
        <div id="grooming-palette-results" role="listbox" className="max-h-[360px] overflow-auto py-1.5">
          {results.length === 0 ? (
            <p className={cn("px-[18px] py-3 text-[13px]", TOOLBOX_TEXT.faint)}>
              {backlog.isFetching ? "Searching the backlog…" : "Nothing matches."}
            </p>
          ) : (
            results.map((command, index) => {
              const Icon = command.icon ?? CircleDot;
              const heading = command.section && command.section !== results[index - 1]?.section ? command.section : undefined;
              const twoLine = Boolean(command.description ?? command.action);
              const isActive = index === active;
              return (
                <div key={command.id} role="presentation">
                  {heading && (
                    <div
                      role="presentation"
                      className={cn("px-[18px] pb-1 pt-2 text-[11px] font-medium uppercase tracking-[.06em]", TOOLBOX_TEXT.faint)}
                    >
                      {heading}
                    </div>
                  )}
                  <div
                    id={`palette-${command.id}`}
                    role="option"
                    aria-selected={isActive}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 px-[18px] text-[13px] text-[#D4D4D8]",
                      twoLine ? "min-h-[52px] py-2" : "h-10",
                      isActive && "bg-[#1A1A1E] text-[#FAFAFA]",
                      isActive && twoLine && "shadow-[inset_3px_0_0_#E11837]"
                    )}
                    onMouseEnter={() => {
                      setSelected(index);
                    }}
                    onClick={() => {
                      run(command);
                    }}
                  >
                    <Icon
                      className={cn("h-[15px] w-[15px] flex-none", command.iconClassName ?? TOOLBOX_TEXT.faint)}
                      aria-hidden="true"
                    />
                    {twoLine ? (
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-[14px]">
                          <EmphasisedLabel label={command.label} emphasis={command.emphasis} />
                        </span>
                        {command.description && (
                          <span className="truncate text-[12px] text-[#A1A1AA]">{command.description}</span>
                        )}
                      </span>
                    ) : (
                      <span className="min-w-0 flex-1 truncate">{command.label}</span>
                    )}
                    {command.action ? (
                      <span className="flex-none text-xs font-medium text-[#6CC4FF]">{command.action}</span>
                    ) : command.hint ? (
                      <span className="font-mono text-xs text-[#A1A1AA]">{command.hint}</span>
                    ) : (
                      twoLine &&
                      isActive && (
                        <kbd className="flex-none rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 font-sans text-[11px] text-[#A1A1AA]">
                          Enter
                        </kbd>
                      )
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {linkHint && (
          <p
            className={cn("flex items-center gap-2 border-t border-[#1F1F23] px-[18px] py-[9px] text-xs", TOOLBOX_TEXT.faint)}
            data-testid="palette-link-hint"
          >
            <Info className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            {LINK_FOOTER_HINT}
          </p>
        )}
        <div className={cn("flex items-center gap-3 border-t border-[#1F1F23] px-[18px] py-[9px] text-xs", TOOLBOX_TEXT.faint)}>
          <span>
            <kbd className="rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 font-sans text-[11px]">↑↓</kbd> move
          </span>
          <span>
            <kbd className="rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 font-sans text-[11px]">↵</kbd> open
          </span>
          <span>
            <kbd className="rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 font-sans text-[11px]">Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}

/** A label with one part bold (frame D5: "Open AB#25175 in **SpecTree-Sandbox**"). */
function EmphasisedLabel({ label, emphasis }: { label: string; emphasis?: string | undefined }) {
  const at = emphasis ? label.lastIndexOf(emphasis) : -1;
  if (!emphasis || at < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, at)}
      <strong className="font-semibold text-[#FAFAFA]">{emphasis}</strong>
      {label.slice(at + emphasis.length)}
    </>
  );
}
